import React, { useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { FormContext } from './FormContext'

export type FormProps = Omit<
  React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
  'onSubmit'
> & {
  scope?: string
  extraData?: Record<string, string>
  onSubmit?: (data: FormData) => void
  twoColumnLayout?: boolean
}
export type FormRef = {
  element: React.RefObject<HTMLFormElement>
  getFormData: () => FormData
  reset: () => void
}

const parsleyOptions = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  classHandler({ $element }: any) {
    if ($element.is('select') && $element.parent().is('.bootstrap-select')) {
      return $element.parent()
    }
    return $element
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorsContainer({ $element }: any) {
    return $element.closest('.form-input-input, form-input-textarea')
  }
}

const Form = styled(
  React.forwardRef<FormRef, FormProps>(function Form({ scope, extraData, onSubmit, twoColumnLayout, ...props }, ref) {
    const formRef = useRef<HTMLFormElement>(null)

    const getFormData = useCallback((): FormData => {
      const result = new FormData()
      for (const key in extraData) {
        const name = scope ? `${scope}[${key}]` : key
        result.set(name, extraData[key])
      }
      if (formRef.current) {
        for (const el of Array.from(
          formRef.current.querySelectorAll<
            HTMLTextAreaElement | HTMLInputElement | HTMLButtonElement | HTMLSelectElement
          >('textarea, input, button, select')
        )) {
          if (el.name) {
            result.set(el.name, formRef.current[el.name].value)
          }
        }
      }
      return result
    }, [extraData, scope])

    const reset = useCallback(() => {
      formRef.current?.reset()
    }, [])

    useImperativeHandle(ref, () => ({
      element: formRef,
      getFormData,
      reset
    }))

    useLayoutEffect(() => {
      if (formRef.current && $.fn.parsley) {
        $(formRef.current).parsley(parsleyOptions)
      }
    }, [])

    const handleSubmit = useCallback(
      (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        onSubmit?.(getFormData())
      },
      [getFormData, onSubmit]
    )

    const form = (
      <form
        {...props}
        className={['form', props.className].filter((x) => !!x).join(' ')}
        ref={formRef}
        onSubmit={handleSubmit}
      />
    )

    return (
      <FormContext.Provider
        value={useMemo(
          () => ({
            scope
          }),
          [scope]
        )}
      >
        {twoColumnLayout ? (
          // I didn't write that CSS – don't hit me :D
          <div className="row">
            <div className="col-xs-12">{form}</div>
          </div>
        ) : (
          form
        )}
      </FormContext.Provider>
    )
  })
)`
  .bootstrap-select + .parsley-error-list {
    margin-top: -5px;
    margin-bottom: 5px;
  }
`

export default Form
